import React, { useEffect, useState } from 'react';
import { companyOrgEntitiesService } from '../services/companyOrgEntitiesService';
import { companyOrgService } from '../services/companyOrgService';
import { fileStorageService } from '../services/fileStorageService';
import awsS3Service from '../services/awsS3Service';
import { Blueprint } from '../models/orgModels';
import ExcelDetailView from './ExcelDetailView';
import { shareCSV, sharePDF, shareExcel, shareWord } from '../utils/sharingUtils';

interface Props {
  folderId: string;
  refreshToken?: number;
  onViewDetails?: (blueprint: Blueprint, itemType: 'blueprint') => void;
}

const BlueprintTable: React.FC<Props> = ({ folderId, refreshToken = 0, onViewDetails }) => {
  const [list, setList] = useState<Blueprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const entries: Array<{ docId: string; blueprint: Blueprint }> = [];
        // Scan all blueprint files in this folder and get every blueprint per file
        const files = await companyOrgService.listFilesByFolder(folderId);
        const bpFiles = files.filter((f: any) => f.kind === 'blueprints');
        for (const f of bpFiles) {
          const byFile = await companyOrgEntitiesService.listBlueprintsByFile(f.id!);
          byFile.forEach(x => entries.push(x));
        }
        // Also include any docs saved directly with folderId
        const byFolder = await companyOrgEntitiesService.listBlueprintsByFolder(folderId);
        byFolder.forEach(x => entries.push(x));
        // Dedupe by Firestore docId so every saved doc is shown
        const map = new Map<string, Blueprint>();
        for (const it of entries) {
          if (it && it.docId) map.set(it.docId, { ...it.blueprint, id: it.docId } as any);
        }
        setList(Array.from(map.values()));
      } finally {
        setLoading(false);
      }
    })();
  }, [folderId, refreshToken]);

  const download = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const handleViewDetails = async (blueprint: Blueprint) => {
    if (onViewDetails) {
      const blueprintWithAttachments = await loadAttachmentsData(blueprint);
      onViewDetails(blueprintWithAttachments, 'blueprint');
    } else {
      const blueprintWithAttachments = await loadAttachmentsData(blueprint);
      setSelectedBlueprint(blueprintWithAttachments);
      setShowDetailView(true);
    }
  };

  const handleCloseDetailView = () => {
    setShowDetailView(false);
    setSelectedBlueprint(null);
  };

  // Function to load actual file data for attachments
  const loadAttachmentsData = async (blueprint: Blueprint): Promise<Blueprint> => {
    if (!blueprint.attachments || blueprint.attachments.length === 0) {
      return blueprint;
    }

    console.log('🔍 Loading attachments for blueprint:', blueprint.name);
    console.log('📎 Attachments found:', blueprint.attachments);

    try {
      const loadedAttachments = await Promise.all(
        blueprint.attachments.map(async (attachment) => {
          console.log('📄 Processing attachment:', attachment.name, 'URL:', attachment.url);
          
          // Skip placeholder URLs
          if (attachment.url && attachment.url.includes('via.placeholder.com')) {
            console.log('⚠️ Skipping placeholder URL:', attachment.url);
            return null; // Filter out placeholder attachments
          }
          
          try {
            // Try AWS S3 first, then Firestore fallback
            let fileInfo;
            if (awsS3Service.isConfigured()) {
              fileInfo = await awsS3Service.getFileUrl({
                id: attachment.id,
                name: attachment.name,
                url: attachment.url,
                size: attachment.size_bytes || 0,
                type: attachment.mime_type || 'application/octet-stream',
                uploadedAt: new Date().toISOString(),
                path: '',
                s3Key: attachment.s3Key || '',
                s3Bucket: process.env.REACT_APP_S3_BUCKET_NAME || ''
              });
            } else {
              fileInfo = await fileStorageService.getFileUrl({
                id: attachment.id,
                name: attachment.name,
                url: attachment.url,
                size: attachment.size_bytes || 0,
                type: attachment.mime_type || 'application/octet-stream',
                uploadedAt: new Date().toISOString(),
                path: '',
                base64Data: ''
              });
            }
            
            console.log('✅ Loaded file URL:', fileInfo);
            return {
              ...attachment,
              url: fileInfo
            };
          } catch (error) {
            console.warn(`Failed to load attachment ${attachment.name}:`, error);
            return attachment; // Return original if loading fails
          }
        })
      );

      // Filter out null values (placeholder attachments) and ensure proper typing
      const validAttachments = loadedAttachments.filter((att): att is NonNullable<typeof att> => att !== null);

      console.log('📋 Final attachments:', validAttachments);

      return {
        ...blueprint,
        attachments: validAttachments
      };
    } catch (error) {
      console.error('Error loading attachments:', error);
      return blueprint; // Return original blueprint if loading fails
    }
  };

  const exportCSV = () => {
    if (!list.length) return;
    const headers = ['Name','Category','Version','Created By','Description','Default Tasks'];
    const rows = list.map(bp => [
      bp.name,
      bp.category || '',
      bp.version,
      bp.created_by,
      (bp.description||'').replace(/\n/g,' '),
      (bp.default_tasks||[]).join(' | ')
    ]);
    const csv = [headers, ...rows].map(r => r.map(f => `"${String(f).replace(/"/g,'""')}"`).join(',')).join('\n');
    download(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'blueprints.csv');
  };

  const tableHTML = () => {
    const head = '<tr><th>Name</th><th>Category</th><th>Version</th><th>Created By</th><th>Description</th><th>Default Tasks</th></tr>';
    const body = list.map(bp => `<tr>
      <td>${bp.name}</td>
      <td>${bp.category||''}</td>
      <td>${bp.version}</td>
      <td>${bp.created_by}</td>
      <td>${(bp.description||'').replace(/</g,'&lt;')}</td>
      <td>${(bp.default_tasks||[]).join(', ')}</td>
    </tr>`).join('');
    return `<table border="1" cellspacing="0" cellpadding="6">${head}${body}</table>`;
  };

  const exportExcel = () => {
    download(new Blob([tableHTML()], { type: 'application/vnd.ms-excel' }), 'blueprints.xls');
  };

  const exportPDF = () => {
    const html = `<!doctype html><html><head><meta charset=\"utf-8\" /><title>Blueprints</title>
      <style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f3f4f6}</style>
      </head><body>${tableHTML()}<script>window.print();</script></body></html>`;
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const exportWord = () => {
    download(new Blob([`<!doctype html><html><head><meta charset=\"utf-8\" /></head><body>${tableHTML()}</body></html>`], { type: 'application/msword' }), 'blueprints.doc');
  };

  const handleShareCSV = () => {
    if (!list.length) return;
    const headers = ['Name','Category','Version','Created By','Description','Default Tasks'];
    const data = list.map(bp => [
      bp.name, 
      bp.category||'', 
      bp.version, 
      bp.created_by, 
      (bp.description||'').replace(/\n/g,' '), 
      (bp.default_tasks||[]).join(' | ')
    ]);
    shareCSV(data, headers, 'blueprints-export.csv');
  };

  const handleSharePDF = () => {
    if (!list.length) return;
    const headers = ['Name','Category','Version','Created By','Description','Default Tasks'];
    const data = list.map(bp => ({
      name: bp.name,
      category: bp.category||'',
      version: bp.version,
      created_by: bp.created_by,
      description: (bp.description||'').replace(/\n/g,' '),
      default_tasks: (bp.default_tasks||[]).join(' | ')
    }));
    sharePDF(data, headers, 'blueprints-export.pdf', 'Blueprints Export');
  };

  const handleShareExcel = () => {
    if (!list.length) return;
    const headers = ['Name','Category','Version','Created By','Description','Default Tasks'];
    const data = list.map(bp => ({
      name: bp.name,
      category: bp.category||'',
      version: bp.version,
      created_by: bp.created_by,
      description: (bp.description||'').replace(/\n/g,' '),
      default_tasks: (bp.default_tasks||[]).join(' | ')
    }));
    shareExcel(data, headers, 'blueprints-export.xlsx');
  };

  const handleShareWord = () => {
    if (!list.length) return;
    const headers = ['Name','Category','Version','Created By','Description','Default Tasks'];
    const data = list.map(bp => ({
      name: bp.name,
      category: bp.category||'',
      version: bp.version,
      created_by: bp.created_by,
      description: (bp.description||'').replace(/\n/g,' '),
      default_tasks: (bp.default_tasks||[]).join(' | ')
    }));
    shareWord(data, headers, 'blueprints-export.doc', 'Blueprints Export');
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (!list.length) return <div className="p-4 text-gray-500">No blueprint data</div>;

  return (
    <div className="border rounded-xl bg-white shadow-sm">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b bg-gray-50 gap-4">
        <h3 className="text-lg font-semibold text-gray-800">Blueprints</h3>
        <div className="flex flex-wrap items-center gap-2">
          <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors" onClick={handleShareCSV}>
            <span className="hidden sm:inline">CSV</span>
            <span className="sm:hidden">📊</span>
          </button>
          <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors" onClick={handleShareExcel}>
            <span className="hidden sm:inline">Excel</span>
            <span className="sm:hidden">📈</span>
          </button>
          <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors" onClick={handleSharePDF}>
            <span className="hidden sm:inline">PDF</span>
            <span className="sm:hidden">📄</span>
          </button>
          <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors" onClick={handleShareWord}>
            <span className="hidden sm:inline">Word</span>
            <span className="sm:hidden">📝</span>
          </button>
        </div>
      </div>
      
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Name</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Category</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Version</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Created By</th>
              <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {list.map((bp, idx) => (
              <tr key={idx} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => handleViewDetails(bp)}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900 truncate max-w-xs" title={bp.name}>
                    {bp.name}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {bp.category || 'Uncategorized'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{bp.version}</td>
                <td className="px-4 py-3 text-gray-600">{bp.created_by}</td>
                <td className="px-4 py-3">
                  <button 
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(bp);
                    }}
                    title="View Details"
                  >
                    <span className="mr-1">👁️</span>
                    <span className="hidden sm:inline">View</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        <div className="p-4 space-y-4">
          {list.map((bp, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewDetails(bp)}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold text-gray-900 truncate" title={bp.name}>
                    {bp.name}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">Version {bp.version}</p>
                </div>
                <button 
                  className="ml-3 flex-shrink-0 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(bp);
                  }}
                  title="View Details"
                >
                  <span className="mr-1">👁️</span>
                  <span>View</span>
                </button>
              </div>
              
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {bp.category || 'Uncategorized'}
                </span>
                <span className="text-xs text-gray-500">by {bp.created_by}</span>
              </div>
              
              {bp.description && (
                <p className="text-sm text-gray-600 line-clamp-2" title={bp.description}>
                  {bp.description}
                </p>
              )}
              
              {bp.default_tasks && bp.default_tasks.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-gray-700 mb-1">Default Tasks:</p>
                  <div className="flex flex-wrap gap-1">
                    {bp.default_tasks.slice(0, 3).map((task, taskIdx) => (
                      <span key={taskIdx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                        {typeof task === 'string' ? task : task.title || 'Untitled Task'}
                      </span>
                    ))}
                    {bp.default_tasks.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-500">
                        +{bp.default_tasks.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Excel Detail View */}
      {showDetailView && selectedBlueprint && (
        <ExcelDetailView
          item={{
            ...selectedBlueprint,
            title: selectedBlueprint.name,
            createdBy: selectedBlueprint.created_by,
            version: selectedBlueprint.version,
            description: selectedBlueprint.description,
            attachedFiles: selectedBlueprint.attachments || [],
            attachments: selectedBlueprint.attachments || []
          }}
          itemType="blueprint"
          onClose={handleCloseDetailView}
          onEdit={() => {
            console.log('Edit blueprint:', selectedBlueprint);
          }}
          onDelete={() => {
            console.log('Delete blueprint:', selectedBlueprint);
          }}
        />
      )}
    </div>
  );
};

export default BlueprintTable;
